import { useContext, useState } from "react";
import { Link, useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import { FormControl, IconButton, Input, InputAdornment, InputLabel } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgImage from "assets/images/bg-sign-up-cover.jpeg";

import AuthService from "services/auth-service";
import { AuthContext } from "context";
import {
  SETUP_CODE_PATTERN,
  apiErrorCode,
  apiErrorMessage,
  isSession,
  isSetup,
  passwordProblem,
  setupPayload,
} from "./setupApi";

const UNAVAILABLE_CODES = ["SETUP_LINK_UNAVAILABLE", "INVALID_SETUP_CODE"];

const PasswordField = ({ id, label, name, value, onChange, shown, onToggle }) => (
  <MDBox mb={2}>
    <FormControl fullWidth variant="standard">
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <Input
        id={id}
        type={shown ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        inputProps={{ autoComplete: "new-password", minLength: 8 }}
        required
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={shown ? "hide the password" : "display the password"}
              onClick={onToggle}
              edge="end"
            >
              {shown ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  </MDBox>
);

// Landing page for the SMS short link /s/setup/:code. Opening the page does
// nothing on the server (link previews and scanners are harmless); Continue
// resolves the code to the same token the invitation email carries, and the
// password is then set through the existing password-reset API.
const SetupLinkForm = ({ code }) => {
  const authContext = useContext(AuthContext);
  const [setup, setSetup] = useState(null);
  const [inputs, setInputs] = useState({ password: "", password_confirmation: "" });
  const [shown, setShown] = useState({ password: false, confirmation: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(!SETUP_CODE_PATTERN.test(code || ""));
  const [completed, setCompleted] = useState(false);

  const changeHandler = (e) => setInputs({ ...inputs, [e.target.name]: e.target.value });

  const openSetup = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await AuthService.resolveSetupLink(code);
      const attributes = response?.data?.attributes;
      if (!isSetup(attributes)) {
        throw { error: "Unexpected setup response. Please contact support." };
      }
      setSetup({ token: attributes.token, email: attributes.email });
    } catch (err) {
      if (UNAVAILABLE_CODES.includes(apiErrorCode(err))) {
        setUnavailable(true);
      } else {
        setError(apiErrorMessage(err, "Unable to open setup. Please try again."));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!setup || busy || completed) return;
    const problem = passwordProblem(inputs.password, inputs.password_confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    let session;
    try {
      session = await AuthService.resetPassword(
        setupPayload(setup, inputs.password, inputs.password_confirmation)
      );
    } catch (err) {
      setBusy(false);
      if (apiErrorCode(err) === "SETUP_LINK_UNAVAILABLE") {
        setSetup(null);
        setUnavailable(true);
        return;
      }
      setError(apiErrorMessage(err, "Unable to finish setup. Please try again."));
      return;
    }
    // The password is set; never resubmit from here on.
    setCompleted(true);
    setSetup(null);
    setInputs({ password: "", password_confirmation: "" });
    setBusy(false);
    if (isSession(session)) {
      // Stores both tokens and navigates into the app.
      authContext.login(session.access_token, [], session.refresh_token);
    }
  };

  let body;
  if (completed) {
    body = (
      <MDTypography variant="button" color="text">
        Your password is set. If you are not signed in automatically,{" "}
        <MDTypography component={Link} to="/auth/login" variant="button" color="info" fontWeight="medium">
          sign in
        </MDTypography>{" "}
        with your email and new password.
      </MDTypography>
    );
  } else if (unavailable) {
    body = (
      <MDBox>
        <MDTypography variant="h6" mb={1}>
          This setup link is no longer available
        </MDTypography>
        <MDTypography variant="button" color="text" display="block" mb={2}>
          It may have expired, already been used, or been replaced by a newer invitation.
          Ask the person who invited you to send a new invitation, or reset your password
          if your account is already set up.
        </MDTypography>
        <MDButton component={Link} to="/auth/forgot-password" variant="outlined" color="info" fullWidth>
          Reset password
        </MDButton>
      </MDBox>
    );
  } else if (!setup) {
    body = (
      <MDBox>
        <MDTypography variant="button" color="text" display="block" mb={3}>
          Rivio helps you monitor tanks and equipment. Set your password to access what
          has been shared with you.
        </MDTypography>
        {error && (
          <MDTypography variant="caption" color="error" display="block" mb={2} role="alert">
            {error}
          </MDTypography>
        )}
        <MDButton variant="gradient" color="info" fullWidth disabled={busy} onClick={openSetup}>
          {busy ? "Opening..." : "Continue"}
        </MDButton>
      </MDBox>
    );
  } else {
    body = (
      <MDBox component="form" role="form" onSubmit={submitHandler}>
        <MDTypography variant="button" color="text" display="block" mb={2}>
          Set a password for <strong>{setup.email}</strong>.
        </MDTypography>
        <PasswordField
          id="setup-password"
          label="New password"
          name="password"
          value={inputs.password}
          onChange={changeHandler}
          shown={shown.password}
          onToggle={() => setShown({ ...shown, password: !shown.password })}
        />
        <PasswordField
          id="setup-password-confirmation"
          label="Confirm password"
          name="password_confirmation"
          value={inputs.password_confirmation}
          onChange={changeHandler}
          shown={shown.confirmation}
          onToggle={() => setShown({ ...shown, confirmation: !shown.confirmation })}
        />
        {error && (
          <MDTypography variant="caption" color="error" fontWeight="light" role="alert">
            {error}
          </MDTypography>
        )}
        <MDBox mt={4} mb={1}>
          <MDButton variant="gradient" color="info" fullWidth type="submit" disabled={busy}>
            {busy ? "Saving..." : "Finish setup"}
          </MDButton>
        </MDBox>
      </MDBox>
    );
  }

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Set up your Rivio account
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {body}
        </MDBox>
      </Card>
    </CoverLayout>
  );
};

// key={code} resets all state when navigating between two different links.
const SetupLink = () => {
  const { code = "" } = useParams();
  return <SetupLinkForm key={code} code={code} />;
};

export default SetupLink;
